import { BOOK_LANGUAGE_FORM_EN_ID } from '../../Settings/lists/bookLanguageFormSupport';
import type { BookFormDraft, BookPageDraft, BookPageLangContent } from './types';
import { defaultPageLangContent } from './model';

export const BOOK_CREATE_DRAFT_STORAGE_KEY = 'glubes.bookCreateDraft.v1';

type PersistedBookCreateEnvelope = {
  v: 1;
  savedAt: string;
  draft: Record<string, unknown>;
};

function draftToJsonSafe(draft: BookFormDraft): Record<string, unknown> {
  return {
    mode: draft.mode,
    id: draft.id,
    author_id: draft.author_id,
    category_ids: draft.category_ids,
    age_group_ids: draft.age_group_ids,
    writer_emails: draft.writer_emails,
    language_enabled: draft.language_enabled,
    name_map: draft.name_map,
    description_map: draft.description_map,
    cover_image_path: draft.cover_image_path,
    promo_image_path: draft.promo_image_path,
    published: draft.published,
    pages: draft.pages.map((p) => ({
      clientId: p.clientId,
      page_number: p.page_number,
      image_path: p.image_path,
      video_path: p.video_path,
      byLang: Object.fromEntries(
        Object.entries(p.byLang).map(([langId, c]) => [
          langId,
          {
            full_text: c.full_text,
          },
        ]),
      ),
    })),
  };
}

function sanitizeStringRecord(raw: unknown): Record<string, string> {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    out[k] = typeof v === 'string' ? v : v != null ? String(v) : '';
  }
  return out;
}

function reviveBookPageLangContent(raw: unknown): BookPageLangContent {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return defaultPageLangContent();
  }
  const o = raw as Record<string, unknown>;
  if (typeof o.full_text === 'string') {
    return { full_text: o.full_text };
  }
  const textsRaw = o.texts;
  if (Array.isArray(textsRaw)) {
    const parts: string[] = [];
    for (const t of textsRaw) {
      if (t == null || typeof t !== 'object' || Array.isArray(t)) continue;
      const x = t as Record<string, unknown>;
      const text = typeof x.text === 'string' ? x.text.trim() : '';
      if (text) parts.push(text);
    }
    if (parts.length > 0) {
      return { full_text: parts.join('\n\n') };
    }
  }
  return defaultPageLangContent();
}

function reviveBookPageDraft(raw: unknown): BookPageDraft | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const page_number =
    typeof o.page_number === 'number' && Number.isFinite(o.page_number) ? Math.floor(o.page_number) : 1;
  const clientId = typeof o.clientId === 'string' && o.clientId.trim() ? o.clientId : crypto.randomUUID();
  let image_path = typeof o.image_path === 'string' ? o.image_path : '';
  let video_path = typeof o.video_path === 'string' ? o.video_path : '';
  const byLangRaw = o.byLang;
  const byLang: Record<string, BookPageLangContent> = {};
  if (byLangRaw != null && typeof byLangRaw === 'object' && !Array.isArray(byLangRaw)) {
    for (const [langId, v] of Object.entries(byLangRaw as Record<string, unknown>)) {
      if (!langId.trim()) continue;
      byLang[langId] = reviveBookPageLangContent(v);
      if (v != null && typeof v === 'object' && !Array.isArray(v)) {
        const lr = v as Record<string, unknown>;
        const legacyImg = typeof lr.image_path === 'string' ? lr.image_path : '';
        const legacyVid = typeof lr.video_path === 'string' ? lr.video_path : '';
        if (!image_path.trim() && legacyImg.trim()) image_path = legacyImg;
        if (!video_path.trim() && legacyVid.trim()) video_path = legacyVid;
      }
    }
  }
  if (Object.keys(byLang).length === 0) {
    byLang[BOOK_LANGUAGE_FORM_EN_ID] = defaultPageLangContent();
  }
  return { clientId, page_number, image_path, video_path, image_file: null, byLang };
}

export function hasPersistedBookCreateDraft(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    const s = localStorage.getItem(BOOK_CREATE_DRAFT_STORAGE_KEY);
    return s != null && s.length > 2;
  } catch {
    return false;
  }
}

export function bookCreateDraftHasSignificantContent(d: BookFormDraft): boolean {
  if (d.mode !== 'create') return false;
  if (d.author_id.trim()) return true;
  if (d.category_ids.length > 0 || d.age_group_ids.length > 0) return true;
  if (Object.values(d.name_map).some((v) => (v ?? '').trim())) return true;
  if (Object.values(d.description_map).some((v) => (v ?? '').trim())) return true;
  if (d.cover_image_path.trim() || d.promo_image_path.trim()) return true;
  if (d.pages.length > 0) return true;
  return false;
}

export function saveBookCreateDraftToStorage(draft: BookFormDraft, options?: { force?: boolean }): void {
  if (draft.mode !== 'create' || typeof localStorage === 'undefined') return;
  if (!options?.force && !bookCreateDraftHasSignificantContent(draft)) {
    clearBookCreateDraftStorage();
    return;
  }
  try {
    const payload: PersistedBookCreateEnvelope = {
      v: 1,
      savedAt: new Date().toISOString(),
      draft: draftToJsonSafe(draft),
    };
    localStorage.setItem(BOOK_CREATE_DRAFT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota or private mode */
  }
}

export function clearBookCreateDraftStorage(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(BOOK_CREATE_DRAFT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function loadBookCreateDraftFromStorage(): BookFormDraft | null {
  if (typeof localStorage === 'undefined') return null;
  let raw: unknown;
  try {
    const s = localStorage.getItem(BOOK_CREATE_DRAFT_STORAGE_KEY);
    if (s == null || !s.trim()) return null;
    raw = JSON.parse(s) as unknown;
  } catch {
    return null;
  }
  const root = raw as Record<string, unknown>;
  const d = (root.draft ?? raw) as Record<string, unknown>;
  if (d == null || typeof d !== 'object' || Array.isArray(d)) return null;
  if (d.mode !== 'create') return null;

  const pagesRaw = d.pages;
  const pages: BookPageDraft[] = Array.isArray(pagesRaw)
    ? pagesRaw.map(reviveBookPageDraft).filter((p): p is BookPageDraft => p !== null)
    : [];

  const le = d.language_enabled;
  const language_enabled: Record<string, boolean> =
    le != null && typeof le === 'object' && !Array.isArray(le)
      ? Object.fromEntries(
          Object.entries(le as Record<string, unknown>).map(([k, v]) => [k, Boolean(v)]),
        )
      : {};

  return {
    mode: 'create',
    id: typeof d.id === 'string' ? d.id : '',
    author_id: typeof d.author_id === 'string' ? d.author_id : '',
    category_ids: Array.isArray(d.category_ids)
      ? d.category_ids.filter((x): x is string => typeof x === 'string')
      : [],
    age_group_ids: Array.isArray(d.age_group_ids)
      ? d.age_group_ids.filter((x): x is string => typeof x === 'string')
      : [],
    writer_emails: Array.isArray(d.writer_emails)
      ? d.writer_emails
          .map((x) => (typeof x === 'string' ? x.trim().toLowerCase() : String(x ?? '').trim().toLowerCase()))
          .filter((e) => e.length > 0)
      : [],
    language_enabled,
    name_map: sanitizeStringRecord(d.name_map),
    description_map: sanitizeStringRecord(d.description_map),
    cover_image_path: typeof d.cover_image_path === 'string' ? d.cover_image_path : '',
    promo_image_path: typeof d.promo_image_path === 'string' ? d.promo_image_path : '',
    cover_image_file: null,
    promo_image_file: null,
    pages,
    published: typeof d.published === 'boolean' ? d.published : false,
  };
}

export function emptyBookFormDraft(): BookFormDraft {
  return {
    mode: 'create',
    id: '',
    author_id: '',
    category_ids: [],
    age_group_ids: [],
    writer_emails: [],
    language_enabled: {},
    name_map: {},
    description_map: {},
    cover_image_path: '',
    promo_image_path: '',
    cover_image_file: null,
    promo_image_file: null,
    pages: [],
    published: false,
  };
}
